/**
 * Kanvasın çevresine mm cetveli çizer.
 *
 * Fiziksel ölçüde çalışıldığı için gerekli: kullanıcı "bu yazı kenardan 3 mm
 * içeride mi" sorusunu ancak cetvelle yanıtlayabiliyor.
 */

import React from 'react';
import { Box } from '@mui/material';
import { mmToPx } from '../../../types/labelTemplate.types';

export interface LabelRulersProps {
  widthMm: number;
  heightMm: number;
  zoom: number;
  children: React.ReactNode;
}

const RULER_SIZE = 20;

/** Zoom'a göre okunabilir aralık seç — 5 mm çok sıkışırsa 10 mm'ye geç */
function pickStep(pxPerMm: number): number {
  if (pxPerMm >= 6) return 5;
  if (pxPerMm >= 3) return 10;
  return 20;
}

const LabelRulers: React.FC<LabelRulersProps> = ({ widthMm, heightMm, zoom, children }) => {
  const s = mmToPx(1) * zoom;
  const step = pickStep(s);

  const hTicks: React.ReactElement[] = [];
  for (let mm = 0; mm <= widthMm; mm += step) {
    hTicks.push(
      <Box
        key={`h-${mm}`}
        sx={{
          position: 'absolute',
          left: mm * s,
          bottom: 0,
          height: mm % (step * 2) === 0 ? 8 : 4,
          borderLeft: '1px solid',
          borderColor: 'grey.500',
          pointerEvents: 'none',
        }}
      >
        {mm % (step * 2) === 0 && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              left: 2,
              bottom: 7,
              fontSize: 9,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {mm}
          </Box>
        )}
      </Box>,
    );
  }

  const vTicks: React.ReactElement[] = [];
  for (let mm = 0; mm <= heightMm; mm += step) {
    vTicks.push(
      <Box
        key={`v-${mm}`}
        sx={{
          position: 'absolute',
          top: mm * s,
          right: 0,
          width: mm % (step * 2) === 0 ? 8 : 4,
          borderTop: '1px solid',
          borderColor: 'grey.500',
          pointerEvents: 'none',
        }}
      >
        {mm % (step * 2) === 0 && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 1,
              right: 9,
              fontSize: 9,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {mm}
          </Box>
        )}
      </Box>,
    );
  }

  return (
    <Box sx={{ display: 'inline-grid', gridTemplateColumns: `${RULER_SIZE}px auto`, gridTemplateRows: `${RULER_SIZE}px auto` }}>
      <Box sx={{ bgcolor: 'grey.200', borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'grey.400' }} />

      <Box
        sx={{
          position: 'relative',
          height: RULER_SIZE,
          width: widthMm * s,
          bgcolor: 'grey.200',
          borderBottom: '1px solid',
          borderColor: 'grey.400',
        }}
      >
        {hTicks}
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: RULER_SIZE,
          height: heightMm * s,
          bgcolor: 'grey.200',
          borderRight: '1px solid',
          borderColor: 'grey.400',
        }}
      >
        {vTicks}
      </Box>

      <Box>{children}</Box>
    </Box>
  );
};

export default LabelRulers;
